import { getMomentTz } from "@dexteel/mesf-core";
import { EChartsOption } from "echarts-for-react";
import { useMemo } from "react";
import { ProcessData } from "../../../../../models/ProcessData";
import { StrandTimeSeriesData } from "../../../../../models/StrandSpeed";

const colors = [" #E41A1C", " #377EB8", " #4DAF4A", "rgb(196, 174, 209)"];

const moment = getMomentTz();

interface UseChartOptionsProps {
	strandSpeeds: StrandTimeSeriesData[] | null | undefined;
	processData: ProcessData | null | undefined;
}

const strandColors: { [key: string]: string } = {
	S1: colors[0], // Blue
	S2: colors[1], // Orange
	S3: colors[2], // Green
};

export const useChartOptions = ({
	strandSpeeds,
	processData,
}: UseChartOptionsProps): EChartsOption => {
	const chartOptions = useMemo<EChartsOption>(() => {
		const data: StrandTimeSeriesData[] = (strandSpeeds || [])
			.map((temp) => ({
				Datetime: temp.Datetime,
				Value: temp.Value ?? 0,
				TagId: temp.TagId?.toString() || "",
				TagName: temp.TagName || "",
			}))
			.filter((item) => item.Datetime && item.TagId && item.TagName);

		if (data.length === 0) {
			return {
				title: {
					text: "No data available",
					left: "center",
					top: "center",
				},
				series: [],
			};
		}

		const values = data.map((item) => item.Value);
		const minValue = Math.min(...values).toFixed(0);
		const maxValue = Math.max(...values).toFixed(0);

		// Calculate y-axis limits
		const margin = Math.ceil((Number(maxValue) - Number(minValue)) * 0.05);
		const yMin = Math.max(0, Math.floor(Number(minValue)) - margin);
		const yMax = Math.ceil(Number(maxValue) + margin);

		// Calculate quantity of intervals for the y-axis
		const interval = Math.ceil((yMax - yMin) / 5);

		const groupedData = data.reduce<{ [key: string]: StrandTimeSeriesData[] }>(
			(acc, item) => {
				if (!acc[item.TagId]) {
					acc[item.TagId] = [];
				}
				acc[item.TagId].push(item);
				return acc;
			},
			{},
		);

		const tagColorMap: { [tag: string]: string } = {};
		let colorIndex = 0;
		Object.entries(groupedData).forEach(([tagId, tagData]) => {
			// Assign color based on TagName prefix
			const tagName = tagData[0]?.TagName || "";
			const prefix = tagName.substring(0, 2); // Get the first two characters (S1, S2, S3)
			if (strandColors[prefix]) {
				tagColorMap[tagId] = strandColors[prefix];
			} else {
				// Fallback for unexpected TagNames
				tagColorMap[tagId] = colors[colorIndex % colors.length];
				colorIndex++;
			}
		});

		return {
			animation: false,
			toolbox: {
				feature: {
					saveAsImage: {
						name: `Heat ${processData?.HeatName} - Strand Speeds`,
						pixelRatio: 2,
					},
				},
			},
			dataZoom: [
				{
					type: "inside",
					show: true,
				},
			],
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					const datetime = moment(params[0].value[0]).format(
						"yyyy-MM-DD HH:mm:ss",
					);

					const tooltipContent = params
						.map(
							(param: any) =>
								`${param.seriesName}: ${param.value[1].toFixed(1)}`,
						)
						.join("<br>");

					return `Time: ${datetime}<br>${tooltipContent}`;
				},
			},
			legend: {
				data: Object.keys(groupedData)
					.map((tagId) => groupedData[tagId][0].TagName)
					.sort(),
				top: "bottom",
				selectedMode: true,
				textStyle: {
					fontSize: 10,
				},
				itemGap: 20,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					formatter: (value: number) => {
						return moment(value).format("HH:mm");
					},
				},
			},
			yAxis: {
				type: "value",
				name: "IPM",
				nameTextStyle: {
					fontSize: 14,
					align: "left",
				},
				nameLocation: "end",
				min: yMin,
				max: yMax,
				interval: interval,
			},
			series: Object.entries(groupedData).map(([tagId, tagData]) => ({
				name: tagData[0].TagName,
				type: "line",
				smooth: true,
				symbol: "none",
				sampling: "lttb",
				itemStyle: {
					color: tagColorMap[tagId],
				},
				data: tagData.map((item) => [item.Datetime.getTime(), item.Value]),
			})),
		};
	}, [strandSpeeds, processData]);

	return chartOptions;
};
